import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Stack, TextField, MenuItem,
  Select, FormControl, InputLabel, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Tooltip, CircularProgress, Alert,
} from '@mui/material';
import {
  Refresh, Delete, Edit, CheckCircle, HourglassEmpty, FiberNew,
} from '@mui/icons-material';
import { feedbackAPI } from '../services/api';
import PaginationBar from '../components/PaginationBar';
import PageHeader from '../components/PageHeader';

const TYPE_COLORS = {
  suggestion: 'primary',
  query:      'info',
  bug:        'error',
  other:      'default',
  onboarding: 'warning',
};

const STATUS_META = {
  open:        { label: 'Open',        color: 'warning', icon: <FiberNew sx={{ fontSize: 14 }} /> },
  in_progress: { label: 'In Progress', color: 'info',    icon: <HourglassEmpty sx={{ fontSize: 14 }} /> },
  resolved:    { label: 'Resolved',    color: 'success', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
};

export default function Feedbacks() {
  const [items, setItems]         = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [pages, setPages]         = useState(1);
  const [limit]                   = useState(20);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType]     = useState('all');
  const [editItem, setEditItem]   = useState(null);
  const [editStatus, setEditStatus]   = useState('');
  const [editNote, setEditNote]       = useState('');
  const [saving, setSaving]       = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit };
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      if (filterType && filterType !== 'all')   params.type   = filterType;
      const res = await feedbackAPI.getAll(params);
      setItems(res.data.data || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, page, limit]);

  useEffect(() => { load(); }, [load]);
  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [filterStatus, filterType]);

  const openEdit = (item) => {
    setEditItem(item);
    setEditStatus(item.status);
    setEditNote(item.adminNote || '');
  };

  const saveEdit = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      await feedbackAPI.update(editItem._id, { status: editStatus, adminNote: editNote });
      setItems(prev => prev.map(i => i._id === editItem._id
        ? { ...i, status: editStatus, adminNote: editNote }
        : i));
      setEditItem(null);
    } catch (e) {
      setError(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    const id = deleteDialog.id;
    setDeleteDialog({ open: false, id: null });
    try {
      await feedbackAPI.delete(id);
      setItems(prev => prev.filter(i => i._id !== id));
      setTotal(t => t - 1);
    } catch (e) {
      setError('Delete failed');
    }
  };

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="Feedback & Queries"
        subtitle={`Review app feedback, update status, and resolve member queries. ${total} submissions total.`}
        gradient="linear-gradient(90deg, #7C3AED, #A78BFA)"
        actions={
          <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={load} disabled={loading}>Refresh</Button>
        }
      />

      {/* Filters */}
      <Stack direction="row" spacing={2} mb={3} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Type</InputLabel>
          <Select value={filterType} label="Type" onChange={e => setFilterType(e.target.value)}>
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="suggestion">Suggestion</MenuItem>
            <MenuItem value="query">Query</MenuItem>
            <MenuItem value="bug">Bug</MenuItem>
            <MenuItem value="onboarding">Onboarding</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Table */}
      <Paper sx={{ border: 1, borderColor: 'divider' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box textAlign="center" py={6}>
            <Typography color="text.secondary">No feedback found</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Gym</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Message</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(item => {
                  const sm = STATUS_META[item.status] || STATUS_META.open;
                  return (
                    <TableRow key={item._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{item.userName || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.userEmail || ''}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.gymName || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={item.type} color={TYPE_COLORS[item.type] || 'default'} size="small" />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography variant="body2" sx={{
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        }}>
                          {item.message}
                        </Typography>
                        {item.adminNote && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Note: {item.adminNote}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={sm.icon}
                          label={sm.label}
                          color={sm.color}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {new Date(item.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit status / note">
                          <IconButton size="small" onClick={() => openEdit(item)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(item._id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <PaginationBar page={page} pages={pages} total={total} limit={limit} onPage={setPage} />

      {/* Edit dialog */}
      <Dialog open={!!editItem} onClose={() => setEditItem(null)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>Update Feedback</DialogTitle>
        <DialogContent>
          {editItem && (
            <Stack spacing={2} mt={1}>
              <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  FROM: {editItem.userName} ({editItem.userEmail})
                </Typography>
                <Typography variant="body2" mt={0.5}>{editItem.message}</Typography>
              </Box>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={editStatus} label="Status" onChange={e => setEditStatus(e.target.value)}>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Admin Note (optional)"
                multiline
                rows={3}
                size="small"
                value={editNote}
                onChange={e => setEditNote(e.target.value)}
                placeholder="Internal note or reply to user..."
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditItem(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm Dialog ── */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Feedback?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will permanently remove this feedback entry. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
